// 密码重置确认页面 - 现代简约风格
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // 修改这部分：同时支持query参数和hash参数格式
  const token = searchParams.get('token');
  const accessToken = searchParams.get('access_token');
  const refreshToken = searchParams.get('refresh_token');
  const type = searchParams.get('type');

  // 解析hash参数（Supabase标准格式）
  const [hashParams, setHashParams] = useState<URLSearchParams | null>(null);

  useEffect(() => {
    // 解析URL hash参数
    const hash = window.location.hash.substring(1);
    if (hash) {
      const params = new URLSearchParams(hash);
      setHashParams(params);
      console.log('Hash参数解析:', Object.fromEntries(params.entries()));
    }
  }, []);

  useEffect(() => {
    // 获取最终的token值（优先使用hash参数）
    const finalAccessToken = hashParams?.get('access_token') || accessToken;
    const finalRefreshToken = hashParams?.get('refresh_token') || refreshToken;
    const finalType = hashParams?.get('type') || type;
    const finalToken = token;

    console.log('Token验证调试:', {
      hashAccessToken: hashParams?.get('access_token'),
      hashRefreshToken: hashParams?.get('refresh_token'),
      hashType: hashParams?.get('type'),
      queryToken: token,
      queryAccessToken: accessToken,
      queryRefreshToken: refreshToken,
      queryType: type,
      finalAccessToken,
      finalRefreshToken,
      finalType,
      finalToken
    });

    // 修改验证逻辑：检查是否有任一种有效的token格式
    const hasSimpleToken = finalToken && finalType === 'recovery';
    const hasJwtTokens = finalAccessToken && finalRefreshToken && finalType === 'recovery';
    
    if (!hasSimpleToken && !hasJwtTokens) {
      // 只有在hash参数解析完成后才显示错误（避免初始加载时的误报）
      const hasHash = window.location.hash.length > 1;
      if (!hasHash || hashParams !== null) {
        setError(t('resetPassword.invalidOrExpiredResetLink'));
      }
    } else {
      // 清除之前的错误信息
      setError('');
      console.log('Token验证通过:', hasJwtTokens ? 'JWT格式' : '简单Token格式');
    }
  }, [token, accessToken, refreshToken, type, hashParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 验证密码
    if (!password.trim()) {
      setError(t('resetPassword.pleaseEnterNewPassword'));
      return;
    }
    if (password.length < 6) {
      setError(t('resetPassword.passwordMinLength'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('resetPassword.passwordsDoNotMatch'));
      return;
    }

    // 获取最终的token值（优先使用hash参数）
    const finalAccessToken = hashParams?.get('access_token') || accessToken;
    const finalRefreshToken = hashParams?.get('refresh_token') || refreshToken;
    const finalType = hashParams?.get('type') || type;
    const finalToken = token;

    // 修改token验证逻辑
    const hasSimpleToken = finalToken && finalType === 'recovery';
    const hasJwtTokens = finalAccessToken && finalRefreshToken && finalType === 'recovery';
    
    if (!hasSimpleToken && !hasJwtTokens) {
      setError(t('resetPassword.invalidResetLink'));
      return;
    }

    setLoading(true);

    try {
      // 修改API调用：根据token类型发送不同的数据
      const requestData = hasSimpleToken 
        ? { token: finalToken, new_password: password }
        : { access_token: finalAccessToken, refresh_token: finalRefreshToken, new_password: password };

        console.log('发送重置密码请求:', requestData); // 添加这行

      await api.post('/reset-password', requestData);

      console.log('重置密码请求成功'); // 添加这行

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {

      console.log('重置密码请求失败:', err); // 添加这行
      setError(err.response?.data?.error?.message || t('resetPassword.failedToResetPassword'));
    } finally {
      setLoading(false);
    }
  };

  // 修改按钮的disabled条件
  const finalAccessToken = hashParams?.get('access_token') || accessToken;
  const finalRefreshToken = hashParams?.get('refresh_token') || refreshToken;
  const finalType = hashParams?.get('type') || type;
  const finalToken = token;
  
  const isTokenValid = (finalToken && finalType === 'recovery') || (finalAccessToken && finalRefreshToken && finalType === 'recovery');

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8">
          {!success ? (
            <>
              {/* 原有的UI代码保持不变 */}
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-white mb-2">{t('resetPassword.setNewPassword')}</h1>
                <p className="text-white/70 text-sm">{t('resetPassword.enterNewPasswordBelow')}</p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-200 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* 密码输入字段保持不变 */}
                <div className="space-y-2">
                  <label className="block text-white/90 text-sm font-medium">{t('resetPassword.newPassword')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 px-4 py-3 pr-12 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm"
                      placeholder={t('resetPassword.enterNewPasswordPlaceholder')}
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white/80 transition duration-200"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-white/90 text-sm font-medium">{t('resetPassword.confirmPassword')}</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 px-4 py-3 pr-12 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm"
                      placeholder={t('resetPassword.confirmNewPasswordPlaceholder')}
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white/80 transition duration-200"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* 修改按钮的disabled条件 */}
                <button
                  type="submit"
                  disabled={loading || !isTokenValid}
                  className="w-full bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="spinner"></div>
                      {t('resetPassword.updatingPassword')}
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Lock className="h-4 w-4" />
                      <span>{t('resetPassword.updatePassword')}</span>
                    </div>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">Password Updated!</h1>
              <p className="text-white/70 text-sm mb-6">
                Your password has been successfully updated. Redirecting to login page...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;